/**
 * Small markdown-lite toolbar that lives above a Textarea and inserts
 * markdown tokens around the current selection. Companion to the extended
 * renderMiniMarkdown() in LessonBlock.jsx — every token this toolbar emits
 * is a shape the renderer understands.
 *
 * Usage:
 *   const ref = useRef(null);
 *   <RichTextToolbar textareaRef={ref} value={val} onChange={setVal} />
 *   <Textarea ref={ref} value={val} onChange={(e) => setVal(e.target.value)} />
 */
import { useCallback } from "react";
import {
  Heading1, Heading2, Heading3, Heading4,
  Bold, Italic, Strikethrough, Code,
  List, ListOrdered, Quote, FileCode,
  Image as ImageIcon, Link as LinkIcon, Minus, Youtube,
} from "lucide-react";

function ToolButton({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="w-8 h-8 flex items-center justify-center text-slate-600 hover:text-[#0D9488] hover:bg-slate-100 rounded-none transition-colors"
      data-testid={testid}
    >
      <Icon className="w-4 h-4" strokeWidth={2} />
    </button>
  );
}

function Divider() {
  return <div className="w-px h-5 bg-slate-200 mx-0.5" aria-hidden />;
}

export default function RichTextToolbar({ textareaRef, value, onChange }) {
  /**
   * Wrap current selection with `pre`/`post`. If nothing is selected, insert
   * `placeholder` between the markers and select the placeholder so the user
   * can start typing over it.
   */
  const wrap = useCallback((pre, post, placeholder = "") => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const selected = value.slice(start, end);
    const insert = selected || placeholder;
    const next = value.slice(0, start) + pre + insert + post + value.slice(end);
    onChange(next);
    // Restore selection to the inserted text (or placeholder) for immediate editing.
    requestAnimationFrame(() => {
      el.focus();
      const cursorStart = start + pre.length;
      el.setSelectionRange(cursorStart, cursorStart + insert.length);
    });
  }, [textareaRef, value, onChange]);

  /**
   * Prefix current LINE (or every selected line) with `prefix`.
   * Toggle behavior: if the prefix is already present, strip it.
   */
  const prefixLines = useCallback((prefix, { numbered = false } = {}) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    // Expand selection to entire lines.
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    const lineEnd = value.indexOf("\n", end);
    const stop = lineEnd === -1 ? value.length : lineEnd;
    const block = value.slice(lineStart, stop);
    const lines = block.split("\n");
    const stripPattern = numbered ? /^\d+\.\s+/ : new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`);
    const allPrefixed = lines.every((l) => stripPattern.test(l) || l === "");
    let counter = 1;
    const next = lines.map((line) => {
      if (line === "") return line;
      if (allPrefixed) return line.replace(stripPattern, "");
      if (numbered) return `${counter++}. ${line.replace(/^\d+\.\s+/, "")}`;
      // If the exact prefix already leads, don't double up.
      if (line.startsWith(prefix)) return line;
      // If a competing list/heading prefix leads, strip it first for a clean toggle.
      const stripped = line.replace(/^(#{1,6}\s+|-\s+|\d+\.\s+|>\s+)/, "");
      return prefix + stripped;
    }).join("\n");
    const patched = value.slice(0, lineStart) + next + value.slice(stop);
    onChange(patched);
    requestAnimationFrame(() => {
      el.focus();
      const cursor = lineStart + next.length;
      el.setSelectionRange(cursor, cursor);
    });
  }, [textareaRef, value, onChange]);

  /** Insert a block of text at the cursor, on its own line. */
  const insertBlock = useCallback((text) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const needsLeadingNL = before && !before.endsWith("\n") ? "\n" : "";
    const needsTrailingNL = after && !after.startsWith("\n") ? "\n" : "";
    const insert = needsLeadingNL + text + needsTrailingNL;
    onChange(before + insert + after);
    requestAnimationFrame(() => {
      el.focus();
      const cursor = start + insert.length;
      el.setSelectionRange(cursor, cursor);
    });
  }, [textareaRef, value, onChange]);

  const promptForImage = () => {
    const url = window.prompt("Image URL", "https://");
    if (!url) return;
    const alt = window.prompt("Alt text (for accessibility)", "") || "";
    insertBlock(`![${alt}](${url})`);
  };
  const promptForLink = () => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const selected = value.slice(start, end) || "link text";
    const url = window.prompt("Link URL", "https://");
    if (!url) return;
    wrap("[", `](${url})`, selected);
  };
  const promptForVideo = () => {
    const url = window.prompt("YouTube URL", "https://www.youtube.com/watch?v=");
    if (!url) return;
    insertBlock(`@[video](${url})`);
  };

  return (
    <div
      className="flex items-center flex-wrap gap-0.5 border border-slate-200 border-b-0 bg-slate-50 px-1.5 py-1 rounded-none"
      data-testid="rich-text-toolbar"
      // Prevent toolbar clicks from stealing focus from the textarea before wrap() runs.
      onMouseDown={(e) => e.preventDefault()}
    >
      <ToolButton icon={Heading1} label="Heading 1" testid="rtt-h1" onClick={() => prefixLines("# ")} />
      <ToolButton icon={Heading2} label="Heading 2" testid="rtt-h2" onClick={() => prefixLines("## ")} />
      <ToolButton icon={Heading3} label="Heading 3" testid="rtt-h3" onClick={() => prefixLines("### ")} />
      <ToolButton icon={Heading4} label="Heading 4" testid="rtt-h4" onClick={() => prefixLines("#### ")} />
      <Divider />
      <ToolButton icon={Bold} label="Bold" testid="rtt-bold" onClick={() => wrap("**", "**", "bold text")} />
      <ToolButton icon={Italic} label="Italic" testid="rtt-italic" onClick={() => wrap("*", "*", "italic text")} />
      <ToolButton icon={Strikethrough} label="Strikethrough" testid="rtt-strike" onClick={() => wrap("~~", "~~", "strikethrough")} />
      <ToolButton icon={Code} label="Inline code" testid="rtt-code" onClick={() => wrap("`", "`", "code")} />
      <Divider />
      <ToolButton icon={List} label="Bullet list" testid="rtt-ul" onClick={() => prefixLines("- ")} />
      <ToolButton icon={ListOrdered} label="Numbered list" testid="rtt-ol" onClick={() => prefixLines("1. ", { numbered: true })} />
      <ToolButton icon={Quote} label="Quote" testid="rtt-quote" onClick={() => prefixLines("> ")} />
      <ToolButton icon={FileCode} label="Code block" testid="rtt-codeblock" onClick={() => insertBlock("```\ncode here\n```")} />
      <Divider />
      <ToolButton icon={ImageIcon} label="Image" testid="rtt-image" onClick={promptForImage} />
      <ToolButton icon={LinkIcon} label="Link" testid="rtt-link" onClick={promptForLink} />
      <ToolButton icon={Minus} label="Divider" testid="rtt-hr" onClick={() => insertBlock("---")} />
      <ToolButton icon={Youtube} label="YouTube video" testid="rtt-video" onClick={promptForVideo} />
    </div>
  );
}
